import { checkFeature } from '@bybeam/platform-lib/features/check';
import sort from '@bybeam/platform-lib/utilities/sort';
import {
  AccountType,
  Case,
  CaseStatus,
  FeatureName,
  Fulfillment,
  Payee,
  PayeeType,
  Payment,
  PaymentMethod,
  PaymentSchedule,
  PaymentStatus,
  Program,
  ScheduleType,
} from '@bybeam/platform-types';
import { checkProgramHasAnyFeature } from '@platform-ui/utilities/checkFeature.js';
import dayjs from 'dayjs';

export const AccountTypesDisplay = {
  [AccountType.Checking]: 'Checking Account',
  [AccountType.Savings]: 'Savings Account',
};

export const PaymentStatusDisplay = {
  [PaymentStatus.Pending]: 'Pending',
  [PaymentStatus.Authorized]: 'Authorized for Payment',
  [PaymentStatus.Initiated]: 'Paid',
  [PaymentStatus.Success]: 'Paid',
  [PaymentStatus.Failed]: 'Failed',
  [PaymentStatus.Canceled]: 'Canceled',
};

export const ApplicantPaymentStatusDisplay = {
  ...PaymentStatusDisplay,
  [PaymentStatus.Authorized]: 'Authorized',
  [PaymentStatus.Failed]: 'Processing',
};

export const PaymentMethodDisplay = {
  [PaymentMethod.DirectDeposit]: 'Direct Deposit',
  [PaymentMethod.Check]: 'Paper Check',
  [PaymentMethod.PhysicalCard]: 'Physical Prepaid Card',
  [PaymentMethod.VirtualCard]: 'Virtual Prepaid Card',
  [PaymentMethod.Zelle]: 'Zelle',
  [PaymentMethod.External]: 'External',
};

export const PaymentScheduleDisplay = {
  [PaymentSchedule.BiWeekly]: 'Bi-Weekly',
  [PaymentSchedule.Monthly]: 'Monthly',
  [PaymentSchedule.SemiMonthly]: 'Semi-Monthly',
  [PaymentSchedule.Weekly]: 'Weekly',
  [PaymentSchedule.OneTime]: 'One-time',
};

export const PayeeTypeDisplay = {
  [PayeeType.User]: 'Applicant',
  [PayeeType.Vendor]: 'Vendor',
};

export const getExternalPaymentStatusDisplay = (status: PaymentStatus): string =>
  status === PaymentStatus.Failed ? 'Voided' : PaymentStatusDisplay[status];

export function getApplicantFacingPaymentStatus(
  status: PaymentStatus,
  isPartnerIssued: boolean,
): string {
  if (!isPartnerIssued && status === PaymentStatus.Failed) return PaymentStatusDisplay[status];
  return ApplicantPaymentStatusDisplay[status];
}

export const getApplicantOverallRecurringPaymentStatus = (
  latestPayment: Payment,
  totalInitiatedPaymentCount: number,
  totalPaymentCount: number,
): string => {
  if (latestPayment) {
    return `${totalInitiatedPaymentCount} of ${totalPaymentCount} - ${getApplicantFacingPaymentStatus(
      latestPayment.status,
      true,
    )}`;
  }
  return `1 of ${totalPaymentCount} - Scheduled`;
};

export const getApplicantIndividualRecurringPaymentStatus = (
  payment: Payment,
  paymentIdx: number,
  totalPaymentCount: number,
): string => {
  return `${paymentIdx + 1} of ${totalPaymentCount} - ${
    payment.initiatedAt ? getApplicantFacingPaymentStatus(payment.status, true) : 'Scheduled'
  }`;
};

export const findLatestPayment = (payments: Payment[]): Payment => {
  const initiatedPayments = payments?.filter((payment) => !!payment.initiatedAt);
  if (!initiatedPayments?.length) return undefined;
  return sort(initiatedPayments, {
    accessor: ({ initiatedAt }) => dayjs(initiatedAt),
    ascending: false,
  })[0];
};

export const getTotalInitiatedPaymentCount = (payments?: Payment[]): number => {
  if (!payments?.length) return 0;
  return payments.filter((payment) => !!payment.initiatedAt).length;
};

export const hasValidScheduledPayments = (payments: Payment[]): boolean => {
  if (!payments?.length) return false;
  if (payments.length === 1 && payments[0].status === PaymentStatus.Pending) return false;
  return payments.every(({ scheduledFor }) => !!scheduledFor);
};

export const didPaymentSucceed = (payment: Payment): boolean =>
  [PaymentStatus.Success, PaymentStatus.Initiated].includes(payment?.status);

export const hasAuthorizedPayment = (fulfillment: Fulfillment): boolean => {
  return !!fulfillment?.payments?.find((payment) => payment.status === PaymentStatus.Authorized);
};

export const getNextScheduledDate = (payments: Payment[]): Date => {
  if (!payments?.length) return undefined;
  const sortedPayments = sort(payments, {
    accessor: ({ scheduledFor }) => dayjs(scheduledFor),
    ascending: true,
  });
  return sortedPayments.find((payment) => payment.status === PaymentStatus.Authorized)
    ?.scheduledFor;
};

export const displayPaymentDetails = ({
  program,
  case: case_,
  fulfillment,
  payee,
}: { program: Program; case: Case; fulfillment: Fulfillment; payee?: Payee }) => {
  const hasValidCaseStatus = () =>
    [CaseStatus.Approved, CaseStatus.PaymentSent].includes(case_.status);

  const hasValidPaymentStatus = () => {
    const payments = fulfillment?.payments ?? [];
    if (
      checkFeature(program?.features, FeatureName.PaymentsRecurring) &&
      fulfillment?.scheduleType === ScheduleType.Recurring
    )
      return hasValidScheduledPayments(payments);
    if (
      checkProgramHasAnyFeature(program, [
        FeatureName.PaymentsPartnerIssued,
        FeatureName.PaymentsClaimFunds,
      ])
    ) {
      const payment = findLatestPayment(payments);
      return !!payment && didPaymentSucceed(payment);
    }
    return false;
  };

  const hasValidPayee = () =>
    payee ? fulfillment?.payments?.some((payment) => payment.payee.id === payee.id) : true;

  return hasValidCaseStatus() && hasValidPaymentStatus() && hasValidPayee();
};

export const getPaymentStatus = (program: Program, fulfillment: Fulfillment): string => {
  const hasAnyPaymentFeature = checkProgramHasAnyFeature(program, [
    FeatureName.PaymentsPartnerIssued,
    FeatureName.PaymentsClaimFunds,
    FeatureName.PaymentsRecurring,
  ]);

  if (!hasAnyPaymentFeature || !fulfillment?.payments?.length) return null;

  if (
    checkFeature(program?.features, FeatureName.PaymentsRecurring) &&
    fulfillment.scheduleType === ScheduleType.Recurring
  ) {
    const payment = findLatestPayment(fulfillment?.payments);
    const totalPaymentCount = fulfillment?.paymentPattern?.count ?? 0;
    const totalInitiatedPaymentCount = getTotalInitiatedPaymentCount(fulfillment?.payments);
    const hasScheduledPayment = hasValidScheduledPayments(fulfillment?.payments);
    return (
      hasScheduledPayment &&
      getApplicantOverallRecurringPaymentStatus(
        payment,
        totalInitiatedPaymentCount,
        totalPaymentCount,
      )
    );
  }

  return getApplicantFacingPaymentStatus(
    fulfillment.payments[0].status,
    checkFeature(program?.features, FeatureName.PaymentsPartnerIssued),
  );
};
